import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Checkbox, DatePicker, Modal, Radio, Space, TimePicker, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { RollMode, RollPlanStatus } from '../../../services/pairTradingApi';
import {
  countdownLabel,
  formatIsoDate,
  planSummary,
  rollModeHelp,
  rollModeLabel,
} from '../../../utils/rollPlanView';

/**
 * Choose when the futures leg rolls — or that it does not.
 *
 * The default (AUTO) rolls on the last trading day BEFORE expiry at 14:00, which
 * is the safe answer: expiry-day liquidity drains into the settlement auction and
 * the F1→F2 basis turns erratic there, so a roll placed on expiry day can pay for
 * the privilege.
 *
 * OFF is a real choice — a pair meant to end before expiry has nothing to carry
 * forward — but it is the one that leads to the state nothing else can fix, so it
 * takes an explicit acknowledgement. The BE enforces that too; the checkbox is
 * there so the operator reads the sentence, not to be the only guard.
 *
 * The picker is bounded by dates the SERVER resolved (today … expiry). Nothing
 * here computes an expiry: the browser has no holiday calendar, and a UI that
 * guesses one disagrees with the engine in exactly the months a holiday moves it.
 */
export interface RollPlanModalProps {
  open: boolean;
  onClose: () => void;
  status: RollPlanStatus | null;
  saving?: boolean;
  onSave: (body: {
    mode: RollMode;
    roll_date?: string;
    roll_time?: string;
    acknowledge_settlement?: boolean;
  }) => Promise<void>;
}

const GREY = '#848E9C';

const RollPlanModal: React.FC<RollPlanModalProps> = ({ open, onClose, status, saving, onSave }) => {
  const [mode, setMode] = useState<RollMode>('AUTO');
  const [date, setDate] = useState<dayjs.Dayjs | null>(null);
  const [time, setTime] = useState<dayjs.Dayjs | null>(dayjs('14:00', 'HH:mm'));
  const [acknowledged, setAcknowledged] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-seed from the server every time the modal opens: the plan may have moved
  // (a roll happened, another session edited it) since it was last rendered.
  useEffect(() => {
    if (!open || !status) return;
    setMode(status.plan.mode);
    setDate(status.plan.rollDate ? dayjs(status.plan.rollDate) : null);
    setTime(dayjs(status.plan.rollTime, 'HH:mm'));
    setAcknowledged(status.plan.mode === 'OFF');
    setError(null);
  }, [open, status]);

  const bounds = useMemo(() => {
    const today = status?.today ? dayjs(status.today) : null;
    const expiry = status?.plan.expiry ? dayjs(status.plan.expiry) : null;
    return { today, expiry };
  }, [status]);

  const disabledDate = (value: dayjs.Dayjs) => {
    if (!bounds.today || !bounds.expiry) return false;
    return value.isBefore(bounds.today, 'day') || value.isAfter(bounds.expiry, 'day');
  };

  const submit = async () => {
    setError(null);
    try {
      await onSave({
        mode,
        roll_date: mode === 'MANUAL' && date ? date.format('YYYY-MM-DD') : undefined,
        roll_time: time ? time.format('HH:mm') : undefined,
        acknowledge_settlement: mode === 'OFF' ? acknowledged : undefined,
      });
      onClose();
    } catch (e: any) {
      // The BE's messages name the fix (date in the past, after expiry, OFF
      // without acknowledgement) — show them verbatim.
      setError(e?.response?.data?.message ?? e?.message ?? 'could not save the roll plan');
    }
  };

  const canSubmit =
    !!status &&
    (mode !== 'MANUAL' || !!date) &&
    (mode !== 'OFF' || acknowledged);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Futures roll"
      footer={null}
      width={520}
      destroyOnClose
    >
      <div style={{ fontSize: 12, color: '#EAECEF', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ color: GREY }}>Contract</span>
          <span>
            <strong>{status?.plan.contract ?? '—'}</strong>
            {status && (
              <span style={{ color: GREY, marginLeft: 6 }}>
                expires {formatIsoDate(status.plan.expiry)} · {countdownLabel(status)}
              </span>
            )}
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ color: GREY }}>Now</span>
          <span>{status ? planSummary(status) : '—'}</span>
        </div>

        <Radio.Group value={mode} onChange={(e) => setMode(e.target.value)}>
          <Space direction="vertical">
            {(['AUTO', 'MANUAL', 'OFF'] as RollMode[]).map((option) => (
              <Radio key={option} value={option}>
                {rollModeLabel(option)}
                <span style={{ color: GREY, marginLeft: 6 }}>
                  {option === 'AUTO' && status?.defaultRollDate
                    ? `— ${formatIsoDate(status.defaultRollDate)} at 14:00`
                    : rollModeHelp(option)}
                </span>
              </Radio>
            ))}
          </Space>
        </Radio.Group>

        {mode === 'MANUAL' && (
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ color: GREY, marginBottom: 4 }}>Roll date</div>
              <DatePicker
                style={{ width: '100%' }}
                value={date}
                onChange={setDate}
                disabledDate={disabledDate}
                allowClear={false}
              />
              <div style={{ color: GREY, fontSize: 11, marginTop: 4 }}>
                Any trading day up to {formatIsoDate(status?.plan.expiry)}. A holiday moves
                forward to the next session.
              </div>
            </div>
            <div style={{ width: 120 }}>
              <div style={{ color: GREY, marginBottom: 4, display: 'flex', gap: 4 }}>
                Time
                <Tooltip title="App-local. 14:00 by default: inside the continuous session, before the ATC auction takes the liquidity a roll needs.">
                  <span style={{ cursor: 'help' }}>ⓘ</span>
                </Tooltip>
              </div>
              <TimePicker
                style={{ width: '100%' }}
                value={time}
                onChange={setTime}
                format="HH:mm"
                minuteStep={5}
                allowClear={false}
              />
            </div>
          </div>
        )}

        {mode === 'OFF' && (
          <Alert
            type="warning"
            showIcon
            style={{ fontSize: 11 }}
            message="Nothing will roll this contract"
            description={
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span>
                  On {formatIsoDate(status?.plan.expiry)} the exchange cash-settles the futures
                  leg. Any stock leg still held stays open with nothing hedging it, and the bot
                  cannot close those pairs — the futures side no longer exists. Rebuilding the
                  pair from broker positions is then the only way back.
                </span>
                <Checkbox checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)}>
                  I understand, and I will close the pair before {formatIsoDate(status?.plan.expiry)}
                </Checkbox>
              </div>
            }
          />
        )}

        {status?.isRunning && (
          <Alert
            type="info"
            showIcon
            style={{ fontSize: 11 }}
            message="The bot is running — this takes effect without a restart."
            description="The worker re-reads its config every second and checks the plan on each tick, so moving the date forward to today applies immediately."
          />
        )}

        {error && <Alert type="error" showIcon message={error} />}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="primary" loading={saving} disabled={!canSubmit} onClick={submit}>
            Save
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default RollPlanModal;
